import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../firebase";

interface Test {
  id: string;
  testName: string;
  class: string;
  batchId: string;
  chapterId?: string;
  testDate?: string;
}
interface Chapter {
  id: string;
  chapterName: string;
}
interface Mark {
  id: string;
  testId: string;
  studentId: string;
  marks: string;
}

const StudentTests: React.FC = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tests, setTests] = useState<Test[]>([]);
  const [chapters, setChapters] = useState<Record<string, string>>({});
  const [marks, setMarks] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        if (!user) return;
        // 1. Get student info
        const usersRef = collection(db, "users");
        const userQ = query(usersRef, where("uid", "==", user.uid));
        const userSnap = await getDocs(userQ);
        if (userSnap.empty) {
          setError("Student info not found.");
          setLoading(false);
          return;
        }
        const student = userSnap.docs[0].data();
        const studentClass = student.class;
        const studentBatchId = student.batchId;
        // 2. Fetch tests assigned to this class and batch
        const testsRef = collection(db, "tests");
        const testsQ = query(
          testsRef,
          where("class", "==", studentClass),
          where("batchId", "==", studentBatchId)
        );
        const testsSnap = await getDocs(testsQ);
        const testList: Test[] = testsSnap.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Test, "id">),
        }));
        setTests(testList);
        // 3. Fetch chapters for chapter names (from chapterIds array)
        const allChapterIds = Array.from(
          new Set(
            testList
              .map((t) =>
                Array.isArray((t as any).chapterIds)
                  ? (t as any).chapterIds[0]
                  : undefined
              )
              .filter(Boolean)
          )
        );
        let chaptersMap: Record<string, string> = {};
        if (allChapterIds.length > 0) {
          // Fetch all chapters for the student's class
          const chaptersRef = collection(db, "chapters");
          const chaptersQ = query(chaptersRef, where("class", "==", studentClass));
          const chaptersSnap = await getDocs(chaptersQ);
          chaptersSnap.docs.forEach((doc) => {
            const data = doc.data();
            // Map by Firestore doc.id
            chaptersMap[doc.id] = data.chapterName;
          });
        }
        setChapters(chaptersMap);
        console.log(chapters);
        // 4. Fetch marks for this student for these tests
        // Need studentId from users collection, not user.uid
        const studentId = userSnap.docs[0].id;
        const testIds = testList.map((t) => t.id);
        let marksMap: Record<string, string> = {};
        if (testIds.length > 0) {
          const marksRef = collection(db, "marks");
          const marksQ = query(
            marksRef,
            where("testId", "in", testIds),
            where("studentId", "==", studentId)
          );
          const marksSnap = await getDocs(marksQ);
          marksSnap.docs.forEach((doc) => {
            const data = doc.data();
            marksMap[data.testId] = data.marks;
          });
        }
        setMarks(marksMap);
      } catch (err) {
        setError("Failed to load tests.");
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }
  if (error) {
    return <div className="text-red-500 text-center py-8">{error}</div>;
  }
  if (tests.length === 0) {
    return (
      <div className="text-gray-600 text-center py-8">
        No tests assigned yet.
      </div>
    );
  }
  return (
    <div className="max-w-2xl mx-auto bg-white rounded shadow p-8">
      <h2 className="text-xl font-semibold mb-4 text-blue-800">
        Your Tests & Results
      </h2>
      <table className="min-w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2">Test Name</th>
            <th className="border px-4 py-2">Chapter Name</th>
            <th className="border px-4 py-2">Obtained Marks</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((test) => (
            <tr key={test.id}>
              <td className="border px-4 py-2">{test.testName}</td>
              <td className="border px-4 py-2">
                {Array.isArray((test as any).chapterIds) &&
                (test as any).chapterIds.length > 0
                  ? chapters[(test as any).chapterIds[0]] ||
                    (test as any).chapterIds[0]
                  : "-"}
              </td>
              <td className="border px-4 py-2 font-semibold text-blue-700">
                {marks[test.id] ?? "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentTests;
